// ============================================================================
// HERO SECTION STATIC DATA - Single Source of Truth (SSOT)
// Updated by prerender script at build time to eliminate blocking API calls
// ============================================================================

export interface HeroOption {
  value: string;
  label: string;
}

export interface HeroTreatment {
  slug: string;
  name: string;
  category: string;
}

// Static cities (manually maintained)
export const HERO_CITIES: HeroOption[] = [
  { value: "any", label: "Any Location" },
  { value: "delhi", label: "Delhi" },
  { value: "gurugram", label: "Gurugram" },
  { value: "mumbai", label: "Mumbai" },
  { value: "bangalore", label: "Bangalore" },
];

// Static categories (updated by prerender)
export const HERO_CATEGORIES: HeroOption[] = [
  { value: "any", label: "All Categories" },
  { value: "cardiac", label: "Cardiac" },
  { value: "cosmetic", label: "Cosmetic" },
  { value: "dental", label: "Dental" },
  { value: "fertility", label: "Fertility" },
  { value: "mental_health", label: "Mental Health" },
  { value: "oncology", label: "Oncology" },
  { value: "ophthalmology", label: "Ophthalmology" },
  { value: "orthopedics", label: "Orthopedics" },
  { value: "rehabilitation", label: "Rehabilitation" },
  { value: "wellness", label: "Wellness" },
];

// Static treatments (from database export - updated as needed)
export const HERO_TREATMENTS: HeroTreatment[] = [
  { slug: "angioplasty", name: "Angioplasty", category: "cardiac" },
  { slug: "heart-failure-management", name: "Heart Failure Management", category: "cardiac" },
  { slug: "angiography", name: "Angiography", category: "cardiac" },
  { slug: "pacemaker-implantation", name: "Pacemaker Implantation", category: "cardiac" },
  { slug: "icd-implant", name: "ICD Implant", category: "cardiac" },
  { slug: "lasik-eye-surgery", name: "LASIK Eye Surgery", category: "ophthalmology" },
  { slug: "cataract-surgery", name: "Cataract Surgery", category: "ophthalmology" },
  { slug: "arthroscopy", name: "Arthroscopy", category: "orthopedics" },
  { slug: "rhinoplasty", name: "Rhinoplasty", category: "cosmetic" },
  { slug: "medical-oncology", name: "Medical Oncology", category: "oncology" },
  { slug: "surgical-oncology", name: "Surgical Oncology", category: "oncology" },
  { slug: "radiation-therapy", name: "Radiation Therapy", category: "oncology" },
  { slug: "bone-marrow-transplant", name: "Bone Marrow Transplant", category: "oncology" },
  { slug: "head-neck-cancer-surgery", name: "Head & Neck Cancer Surgery", category: "oncology" },
  { slug: "glaucoma-treatment", name: "Glaucoma Treatment", category: "ophthalmology" },
  { slug: "corneal-transplant", name: "Corneal Transplant", category: "ophthalmology" },
  { slug: "breast-augmentation", name: "Breast Augmentation", category: "cosmetic" },
  { slug: "face-lift", name: "Face Lift", category: "cosmetic" },
  { slug: "liposuction", name: "Liposuction", category: "cosmetic" },
  { slug: "hair-transplant", name: "Hair Transplant", category: "cosmetic" },
  { slug: "tummy-tuck", name: "Tummy Tuck", category: "cosmetic" },
  { slug: "panchakarma-therapy", name: "Panchakarma Therapy", category: "wellness" },
  { slug: "breast-cancer-treatment", name: "Breast Cancer Treatment", category: "oncology" },
  { slug: "total-hip-replacement", name: "Total Hip Replacement", category: "orthopedics" },
  { slug: "total-knee-replacement", name: "Total Knee Replacement", category: "orthopedics" },
  { slug: "trauma-fracture-care", name: "Trauma & Fracture Care", category: "orthopedics" },
  { slug: "spine-decompression-fusion", name: "Spine Decompression & Fusion", category: "orthopedics" },
  { slug: "robotic-joint-replacement", name: "Robotic Joint Replacement", category: "orthopedics" },
  { slug: "shoulder-replacement", name: "Shoulder Replacement", category: "orthopedics" },
  { slug: "cardiac-rehabilitation", name: "Cardiac Rehabilitation", category: "cardiac" },
  { slug: "prostate-cancer-treatment", name: "Prostate Cancer Treatment", category: "oncology" },
  { slug: "retina-surgery", name: "Retina Surgery", category: "ophthalmology" },
  { slug: "ayurveda-treatment", name: "Ayurveda Treatment", category: "wellness" },
  { slug: "yoga-meditation-retreat", name: "Yoga & Meditation Retreat", category: "wellness" },
  { slug: "naturopathy", name: "Naturopathy", category: "wellness" },
  { slug: "lifestyle-disease-management", name: "Lifestyle Disease Management", category: "wellness" },
  { slug: "sports-injury-rehab", name: "Sports Injury Rehab", category: "rehabilitation" },
  { slug: "depression-anxiety-treatment", name: "Depression & Anxiety Treatment", category: "mental_health" },
  { slug: "substance-abuse-rehabilitation", name: "Substance Abuse Rehabilitation", category: "mental_health" },
  { slug: "psychotherapy-counseling", name: "Psychotherapy & Counseling", category: "mental_health" },
  { slug: "all-on-4-implants", name: "All-on-4 Dental Implants", category: "dental" },
  { slug: "penile-implant-surgery", name: "Penile Implant Surgery", category: "wellness" },
  { slug: "post-surgery-rehabilitation", name: "Post-Surgery Rehabilitation", category: "rehabilitation" },
  { slug: "pain-management", name: "Pain Management", category: "rehabilitation" },
  { slug: "smile-makeover", name: "Smile Makeover", category: "dental" },
  { slug: "stroke-rehabilitation", name: "Stroke Rehabilitation", category: "rehabilitation" },
  { slug: "stress-burnout-programs", name: "Stress & Burnout Programs", category: "mental_health" },
  { slug: "dental-implants", name: "Dental Implants", category: "dental" },
  { slug: "porcelain-veneers", name: "Porcelain Veneers", category: "dental" },
  { slug: "heart-bypass-cabg", name: "Heart Bypass Surgery (CABG)", category: "cardiac" },
  { slug: "knee-replacement", name: "Knee Replacement", category: "orthopedics" },
  { slug: "hip-replacement", name: "Hip Replacement", category: "orthopedics" },
  { slug: "ivf-treatment", name: "IVF Treatment", category: "fertility" },
];

// Helper to get filtered treatments by category
export function getFilteredTreatments(category: string): HeroOption[] {
  const filtered = category === "any" 
    ? HERO_TREATMENTS 
    : HERO_TREATMENTS.filter(t => t.category === category);
  
  return [
    { value: "any", label: "All Treatments" },
    ...filtered.map(t => ({ value: t.slug, label: t.name })),
  ];
}

// Helper to find treatment by slug
export function findTreatmentBySlug(slug: string): HeroTreatment | undefined {
  return HERO_TREATMENTS.find(t => t.slug === slug);
}
